import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, ArrowLeftIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { ScrollManager } from '../utils/scrollManager';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { getScoreColors } from '../utils/colorUtils';
import HypothesesList from '../components/hypotheses/HypothesesList';
import ProblemForm from '../components/ProblemForm';
import PermissionGate from '../components/PermissionGate';
import ReadOnlyBadge from '../components/ReadOnlyBadge';
import { Problem, Hypothesis, Project } from '../types';

// Component for displaying associated hypotheses
const AssociatedHypotheses: React.FC<{ problemId: string }> = ({ problemId }) => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchHypotheses = async () => {
      try {
        setIsLoading(true);
        const data = await get<Hypothesis[]>(`/hypotheses?problem_id=${problemId}`);
        setHypotheses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching hypotheses:', err);
        setHypotheses([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHypotheses();
  }, [get, problemId]);

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">AI Hypotheses</h2>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">AI Hypotheses</h2>
      
      {hypotheses.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-gray-500 mb-4">No hypotheses associated with this problem</p>
          <PermissionGate requireWrite>
            <button
              onClick={() => navigate(`/app-hypotheses/new?problemId=${problemId}`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Hypothesis for this Problem
            </button>
          </PermissionGate>
        </div>
      ) : (
        <div className="space-y-4">
          {hypotheses.map((hypothesis) => (
            <div key={hypothesis.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">
                    {hypothesis.title}
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColors(hypothesis.priority_score || 0).background} ${getScoreColors(hypothesis.priority_score || 0).text}`}>
                      Score: {hypothesis.priority_score?.toFixed(2) || 'N/A'}
                    </span>
                  </h3>
                  <MarkdownRenderer content={hypothesis.hypothesis_statement} className="mt-1 text-sm text-gray-600" />
                  <div className="mt-2 grid grid-cols-3 gap-4 text-sm text-gray-500">
                    <span>Impact: {hypothesis.business_impact}/10</span>
                    <span>Feasibility: {hypothesis.technical_feasibility}/10</span>
                    <span>Effort: {hypothesis.implementation_effort}/10</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/app-hypotheses/${hypothesis.id}`)}
                  className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}

          <PermissionGate requireWrite>
            <div className="mt-4">
              <button
                onClick={() => navigate(`/app-hypotheses/new?problemId=${problemId}`)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Hypothesis for this Problem
              </button>
            </div>
          </PermissionGate>
        </div>
      )}
    </div>
  );
};

const ProblemsPage: React.FC = () => {
  const { get, post, put, delete: deleteProblem } = useApi();
  const { canWrite, isViewer } = useAuth();
  const { socket, isConnected } = useWebSocket();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [problems, setProblems] = useState<Problem[]>([]);
  const [selectedProblem, setSelectedProblem] = useState<Problem | null>(null);
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [expandedProblemId, setExpandedProblemId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Filter state hook - must be at component top level
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<string>('');

  // Determine current view based on URL
  const isCreateView = location.pathname === '/app-problems/new';
  const isDetailView = !!id && !isCreateView;
  
  // Check if we should auto-enter edit mode from report
  const searchParams = new URLSearchParams(location.search);
  const shouldAutoEdit = searchParams.get('edit') === 'true';
  
  // Debug logging
  console.log('ProblemsPage Debug:', { 
    id, 
    pathname: location.pathname, 
    isDetailView, 
    isCreateView, 
    isLoading, 
    projectsLength: projects.length,
    shouldAutoEdit
  });

  // Fetch problems and hypotheses from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [problemsData, hypothesesData, projectsData] = await Promise.all([
          get<Problem[]>('/problems'),
          get<Hypothesis[]>('/hypotheses'),
          get<Project[]>('/projects')
        ]);
        
        setProblems(Array.isArray(problemsData) ? problemsData : []);
        setHypotheses(Array.isArray(hypothesesData) ? hypothesesData : []);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        
        // If viewing a specific problem, fetch its details
        if (id && id !== 'new') {
          const problem = await get<Problem>(`/problems/${id}`);
          setSelectedProblem(problem);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [get, id]);

  // Auto-enter edit mode when coming from report
  useEffect(() => {
    if (shouldAutoEdit && selectedProblem && !isLoading) {
      setIsEditing(true);
      // Remove the edit parameter from URL to clean it up
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [shouldAutoEdit, selectedProblem, isLoading]);

  // Don't show loading spinner for create view if we have projects data
  const shouldShowLoading = isLoading && !(isCreateView && projects.length > 0);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleProblemCreated = (data: { problem: Problem }) => {
      console.log('Problem created, updating problems list');
      setProblems(prev => [...prev, data.problem]);
    };

    const handleProblemUpdated = (data: { problem: Problem }) => {
      console.log('Problem updated, updating problems list');
      setProblems(prev => 
        prev.map(p => p.id === data.problem.id ? data.problem : p)
      );
      // Update selected problem if it's the one being viewed
      if (selectedProblem?.id === data.problem.id) {
        setSelectedProblem(data.problem);
      }
    };

    const handleProblemDeleted = (data: { problem_id: string }) => {
      console.log('Problem deleted, updating problems list');
      setProblems(prev => prev.filter(p => p.id !== data.problem_id));
      // If viewing the deleted problem, navigate back to list
      if (selectedProblem?.id === data.problem_id) {
        navigate('/app-problems');
      }
    };

    const handleHypothesisCreated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis created, updating hypotheses list');
      setHypotheses(prev => [...prev, data.hypothesis]);
    };

    const handleHypothesisUpdated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis updated, updating hypotheses list');
      setHypotheses(prev => 
        prev.map(h => h.id === data.hypothesis.id ? data.hypothesis : h)
      );
    };

    const handleHypothesisDeleted = (data: { hypothesis_id: string }) => {
      console.log('Hypothesis deleted, updating hypotheses list');
      setHypotheses(prev => prev.filter(h => h.id !== data.hypothesis_id));
    };

    // Register event listeners
    socket.on('problem_created', handleProblemCreated);
    socket.on('problem_updated', handleProblemUpdated);
    socket.on('problem_deleted', handleProblemDeleted);
    socket.on('hypothesis_created', handleHypothesisCreated);
    socket.on('hypothesis_updated', handleHypothesisUpdated);
    socket.on('hypothesis_deleted', handleHypothesisDeleted);

    // Cleanup listeners on unmount
    return () => {
      socket.off('problem_created', handleProblemCreated);
      socket.off('problem_updated', handleProblemUpdated);
      socket.off('problem_deleted', handleProblemDeleted);
      socket.off('hypothesis_created', handleHypothesisCreated);
      socket.off('hypothesis_updated', handleHypothesisUpdated);
      socket.off('hypothesis_deleted', handleHypothesisDeleted);
    };
  }, [socket, isConnected, selectedProblem, navigate]);

  // Form submission handlers
  const handleCreateProblem = async (problemData: Problem) => {
    try {
      console.log('Creating problem with data:', problemData);
      const newProblem = await post<Problem>('/problems', problemData);
      console.log('Problem created successfully:', newProblem);
      // Don't add to state here - let WebSocket handle it to avoid duplication
      navigate('/app-problems');
    } catch (error) {
      console.error('Error creating problem:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      setError('Failed to create problem. Please try again.');
    }
  };

  const handleUpdateProblem = async (problemData: Problem) => {
    try {
      const updatedProblem = await put<Problem>(`/problems/${problemData.id}`, problemData);
      setProblems(prev => 
        prev.map(p => p.id === updatedProblem.id ? updatedProblem : p)
      );
      setSelectedProblem(updatedProblem);
      setIsEditing(false);
      
      // Check if we need to return to report
      if (ScrollManager.hasPendingReturn()) {
        navigate('/reports');
      }
    } catch (error) {
      console.error('Error updating problem:', error);
      setError('Failed to update problem. Please try again.');
    }
  };

  // KNOWN ISSUE: Delete confirmation modal has timing delay
  // Despite using separate problemToDelete state (same pattern as working hypothesis delete),
  // the modal still appears with a delay after navigation. Root cause unknown.
  // Functionality works correctly, just with UX timing issue.
  const handleDeleteClick = (problem: Problem) => {
    setSelectedProblem(problem);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedProblem) return;
    
    try {
      await deleteProblem(`/problems/${selectedProblem.id}`);
      setProblems(problems.filter((p) => p.id !== selectedProblem.id));
      setShowDeleteModal(false);
      setSelectedProblem(null);
      navigate('/app-problems');
    } catch (err) {
      console.error('Error deleting problem:', err);
      setError('Failed to delete problem. Please try again.');
    }
  };


  if (shouldShowLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading problems...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p className="font-medium">Error loading problems</p>
        <p className="mt-1 text-sm">{error}</p>
      </div>
    );
  }

  // Create Problem View
  if (isCreateView) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => navigate('/app-problems')}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Problems
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Create New Business Problem</h1>
          <p className="mt-1 text-sm text-gray-600">
            Define business problems that could benefit from AI solutions
          </p>
        </div>
        <ProblemForm
          projects={projects}
          onSubmit={handleCreateProblem}
          onCancel={() => navigate('/app-problems')}
        />
      </div>
    );
  }

  // Problem Detail View
  if (isDetailView && selectedProblem) {
    if (isEditing) {
      return (
        <div>
          <div className="mb-6">
            <button
              onClick={() => setIsEditing(false)}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Problem
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Edit Problem</h1>
          </div>
          <ProblemForm
            problem={selectedProblem}
            projects={projects}
            onSubmit={handleUpdateProblem}
            onCancel={() => setIsEditing(false)}
            isEditing={true}
          />
        </div>
      );
    }

    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => navigate('/app-problems')}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Problems
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{selectedProblem.title}</h1>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColors(selectedProblem.priority).background} ${getScoreColors(selectedProblem.priority).text}`}>
                  Priority: {selectedProblem.priority}/10
                </span>
                <span className="text-sm text-gray-500">Impact: {selectedProblem.impact_area}</span>
                {isViewer() && <ReadOnlyBadge />}
              </div>
            </div>
            <PermissionGate requireWrite>
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteClick(selectedProblem)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            </PermissionGate>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Description</h2>
          <MarkdownRenderer content={selectedProblem.description} />
        </div>

        {selectedProblem.stakeholders && selectedProblem.stakeholders.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Stakeholders</h2>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedProblem.stakeholders.map((stakeholder, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stakeholder.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stakeholder.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selectedProblem.metrics && selectedProblem.metrics.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Metrics</h2>
            <div className="space-y-4">
              {selectedProblem.metrics.map((metric, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900">{metric.name}</h3>
                  <p className="mt-1 text-sm text-gray-600">{metric.description}</p>
                  <div className="mt-2 grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Current Value</div>
                      <div className="font-medium">{metric.current_value ?? 'Not set'} {metric.unit}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Target Value</div>
                      <div className="font-medium">{metric.target_value ?? 'Not set'} {metric.unit}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Unit</div>
                      <div className="font-medium">{metric.unit || 'No unit'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Associated Hypotheses */}
        <AssociatedHypotheses problemId={selectedProblem.id} />
      </div>
    );
  }

  // Problems List View (default)
  // Filter problems by selected project
  const filteredProblems = selectedProjectFilter 
    ? problems.filter(p => p.project_id === selectedProjectFilter)
    : problems;

  // Get unique projects that have problems
  const projectsWithProblems = projects.filter(project => 
    problems.some(problem => problem.project_id === project.id)
  );

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Business Problems/Opportunities</h1>
          <p className="mt-1 text-sm text-gray-500">
            Define and manage business problems that could benefit from AI solutions
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {isViewer() && <ReadOnlyBadge />}
          <PermissionGate requireWrite>
            <button
              onClick={() => navigate('/app-problems/new')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Add New Problem
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Project Filter and Context Links */}
      <div className="bg-white shadow-sm rounded-lg p-4 mb-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="project-filter" className="text-sm font-medium text-gray-700">
                Filter by Project:
              </label>
              <select
                id="project-filter"
                value={selectedProjectFilter}
                onChange={(e) => setSelectedProjectFilter(e.target.value)}
                className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">All Projects</option>
                {projectsWithProblems.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedProjectFilter && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  Showing {filteredProblems.length} problem{filteredProblems.length !== 1 ? 's' : ''} for
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {projects.find(p => p.id === selectedProjectFilter)?.name}
                </span>
              </div>
            )}
          </div>

          {selectedProjectFilter && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate(`/app-projects?view=detail&id=${selectedProjectFilter}`)}
                className="inline-flex items-center px-3 py-1.5 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
              >
                📋 View in Project Context
              </button>
              <button
                onClick={() => setSelectedProjectFilter('')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </div>
      
      {filteredProblems.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto max-w-md">
            <p className="text-gray-500 mb-4">
              {selectedProjectFilter
                ? `No business problems found for the selected project.`
                : canWrite()
                  ? `No business problems defined yet. Create one to get started!`
                  : `No business problems defined yet.`
              }
            </p>
            <PermissionGate requireWrite>
              <button
                onClick={() => navigate(`/app-problems/new${selectedProjectFilter ? `?project_id=${selectedProjectFilter}` : ''}`)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {selectedProjectFilter ? 'Create Problem for Project' : 'Create First Problem'}
              </button>
            </PermissionGate>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProblems.map((problem) => {
            const relatedHypotheses = hypotheses.filter(h => h.problem_id === problem.id);
            const hypothesesCount = relatedHypotheses.length;
            const isExpanded = expandedProblemId === problem.id;
            
            return (
              <div key={problem.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <button
                          onClick={() => setExpandedProblemId(isExpanded ? null : problem.id)}
                          className="flex items-center mr-3 text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded ? (
                            <ChevronDownIcon className="h-5 w-5" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5" />
                          )}
                        </button>
                        <div className="flex-1">
                          <button
                            onClick={() => navigate(`/app-problems/${problem.id}`)}
                            className="text-left"
                          >
                            <p className="text-lg font-medium text-indigo-600 hover:text-indigo-900">{problem.title}</p>
                          </button>
                          <div className="mt-1 flex items-center space-x-3">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getScoreColors(problem.priority).background} ${getScoreColors(problem.priority).text}`}>
                              Priority: {problem.priority}/10
                            </span>
                            <span className="text-sm text-gray-500">Impact: {problem.impact_area}</span>
                            {!selectedProjectFilter && problem.project_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/app-projects?view=detail&id=${problem.project_id}`);
                                }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md border border-indigo-200"
                                title="View in project context"
                              >
                                📋 {projects.find(p => p.id === problem.project_id)?.name || 'Unknown Project'}
                              </button>
                            )}
                            <span className="text-sm text-gray-500">Created: {problem.created_at ? new Date(problem.created_at).toLocaleDateString() : 'Unknown'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="font-medium">{hypothesesCount}</span>
                          <span className="ml-1">hypotheses</span>
                        </div>
                        {hypothesesCount > 0 ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/app-hypotheses', { state: { filterProblemId: problem.id } });
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-900 font-medium"
                          >
                            View All
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/app-hypotheses/new?problemId=${problem.id}`);
                            }}
                            className="text-xs text-green-600 hover:text-green-900 font-medium"
                          >
                            + Create First
                          </button>
                        )}
                      </div>
                      
                      <PermissionGate requireWrite>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/app-problems/${problem.id}`);
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(problem);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </PermissionGate>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Problem Description</h4>
                        <MarkdownRenderer content={problem.description} className="text-sm text-gray-700" />
                      </div>
                      
                      {/* Stakeholders */}
                      {problem.stakeholders && problem.stakeholders.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Stakeholders</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {problem.stakeholders.map((stakeholder, index) => (
                              <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="font-medium text-sm text-gray-900">{stakeholder.name}</div>
                                <div className="text-xs text-gray-600">{stakeholder.role}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Metrics */}
                      {problem.metrics && problem.metrics.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Metrics</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {problem.metrics.map((metric, index) => (
                              <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="font-medium text-sm text-gray-900">{metric.name}</div>
                                <div className="text-xs text-gray-600 mt-1">{metric.description}</div>
                                {(metric.current_value !== undefined || metric.target_value !== undefined) && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {metric.current_value !== undefined && `Current: ${metric.current_value}${metric.unit || ''}`}
                                    {metric.current_value !== undefined && metric.target_value !== undefined && ' → '}
                                    {metric.target_value !== undefined && `Target: ${metric.target_value}${metric.unit || ''}`}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Related AI Hypotheses</h4>
                        <HypothesesList
                          hypotheses={hypotheses}
                          filterProblemId={problem.id}
                          emptyMessage={`No hypotheses created for this problem yet.`}
                          createButtonText="Create Hypothesis for this Problem"
                          showCreateButton={true}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProblem && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Problem
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete "{selectedProblem.title}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemsPage;